/**
 * unyc — noyau applicatif (sans framework).
 *
 * Chaque fichier de assets/js/custom/ enregistre un module avec UNYC.register(nom, fn).
 * Les modules sont lancés une fois le DOM prêt, dans l'ordre d'enregistrement, c'est-à-dire
 * l'ordre alphabétique des fichiers (d'où les préfixes numériques). Ce fichier doit rester
 * le premier du dossier.
 *
 * Helpers exposés sur window.UNYC : qs, qsa, scrollOffset, scrollTo, cookie, throttleFrame, ajax.
 */
(function (window, document) {
    'use strict';

    const UNYC = window.UNYC || {};
    const modules = [];
    let started = false;

    function run(module) {
        try {
            module.fn();
        } catch (error) {
            console.error(`[unyc] module « ${module.name} » en erreur :`, error);
        }
    }

    function start() {
        if (started) return;
        started = true;
        modules.forEach(run);
        document.dispatchEvent(new CustomEvent('unyc:ready'));
    }

    /**
     * Enregistre un module. Enregistré après le démarrage, il est lancé immédiatement.
     * @param {string} name Nom du module (logs).
     * @param {Function} fn Initialisation du module.
     */
    UNYC.register = (name, fn) => {
        const module = { name, fn };
        modules.push(module);
        if (started) run(module);
    };

    /* ------------------------------------------------------------------ */
    /* Helpers DOM                                                          */
    /* ------------------------------------------------------------------ */

    UNYC.qs = (selector, root = document) => root.querySelector(selector);
    UNYC.qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

    /**
     * Initialise chaque élément correspondant au sélecteur, une seule fois par module
     * (marqueur data-unyc-init). Permet de relancer un module sans doublonner les listeners.
     */
    UNYC.init = (selector, name, fn) => {
        UNYC.qsa(selector).forEach((el) => {
            const done = (el.dataset.unycInit || '').split(' ').filter(Boolean);
            if (done.includes(name)) return;
            done.push(name);
            el.dataset.unycInit = done.join(' ');
            fn(el);
        });
    };

    /**
     * Vrai si le lien pointe vers une ancre de la page courante.
     */
    UNYC.isSamePageAnchor = (link) =>
        link instanceof HTMLAnchorElement &&
        link.hash.length > 1 &&
        link.origin === window.location.origin &&
        link.pathname === window.location.pathname;

    /**
     * Résout une cible de scroll : "#id", élément, ou null.
     */
    const resolveTarget = (target) => {
        if (target instanceof Element) return target;
        if (typeof target !== 'string' || target.charAt(0) !== '#' || target.length < 2) return null;
        return document.getElementById(decodeURIComponent(target.slice(1)));
    };

    /**
     * Décalage à conserver au-dessus d'une cible de scroll : hauteur du header
     * (+ barre d'admin WordPress). Valeurs reprises de Main.ts / setOffsetY.
     */
    UNYC.scrollOffset = () => {
        const mobile = window.innerWidth < 992;
        if (document.body.classList.contains('admin-bar')) return mobile ? 130 : 144;
        return mobile ? 84 : 112;
    };

    /**
     * Scroll doux vers une ancre ("#id"), un élément ou une position en pixels.
     * Remplace gsap ScrollToPlugin. Retourne false si la cible n'existe pas.
     */
    UNYC.scrollTo = (target, offset = UNYC.scrollOffset()) => {
        let top;

        if (typeof target === 'number') {
            top = target;
        } else {
            const element = resolveTarget(target);
            if (!element) return false;
            top = element.getBoundingClientRect().top + window.pageYOffset - offset;
        }

        window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: 'smooth' });
        return true;
    };

    /**
     * Cookies (remplace js-cookie). Sans `days`, cookie de session, comme js-cookie par défaut.
     */
    UNYC.cookie = {
        get(name) {
            const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
            return match ? decodeURIComponent(match[1]) : undefined;
        },
        set(name, value, days) {
            let cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax`;
            if (typeof days === 'number') {
                cookie += `; expires=${new Date(Date.now() + days * 864e5).toUTCString()}`;
            }
            document.cookie = cookie;
        },
    };

    /**
     * Limite un handler (scroll, resize) à une exécution par frame.
     */
    UNYC.throttleFrame = (fn) => {
        let ticking = false;
        return (...args) => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(() => {
                ticking = false;
                fn(...args);
            });
        };
    };

    /**
     * URL admin-ajax + nonces, injectés par inc/assets.php (wp_add_inline_script, `const AJAX`).
     */
    UNYC.ajax = typeof AJAX !== 'undefined' ? AJAX : {};

    window.UNYC = UNYC;

    /* ------------------------------------------------------------------ */
    /* Démarrage                                                            */
    /* ------------------------------------------------------------------ */

    /*
     * Démarrage dès que le DOM est analysé (custom.min.js est chargé en pied de page).
     */
    if (document.readyState !== 'loading') {
        start();
    } else {
        document.addEventListener('DOMContentLoaded', start);
    }
})(window, document);
